/**
 * Firebase client initialization.
 *
 * Per ~/Documents/Claude/Brain/knowledge/firebase_standards
 * Per ~/Documents/Claude/Brain/brain/12d4dabd.../global_rules.md §4 — Environment Locking
 *
 * All Firebase config comes from VITE_FIREBASE_* env vars. Hardcoded config is forbidden.
 * The expected project ID is asserted by scripts/check-project.js before build.
 */

import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from "firebase/app-check";
import { getAuth, type Auth } from "firebase/auth";
import { initializeFirestore, type Firestore } from "firebase/firestore";
import { getStorage, type FirebaseStorage } from "firebase/storage";
import { getAnalytics, isSupported, type Analytics } from "firebase/analytics";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Validate critical env vars at runtime (defense in depth — check-project.js is the primary gate)
const required: Array<keyof typeof firebaseConfig> = [
  "apiKey",
  "authDomain",
  "projectId",
  "appId",
];
for (const key of required) {
  if (!firebaseConfig[key]) {
    throw new Error(
      `[firebase] Missing required env var: VITE_FIREBASE_${key.replace(/[A-Z]/g, (m) => "_" + m).toUpperCase()}`,
    );
  }
}

// Project ID assertion (catches the case where check-project.js was bypassed somehow)
if (firebaseConfig.projectId !== "drburns-web") {
  throw new Error(
    `[firebase] FATAL: projectId is '${firebaseConfig.projectId}', expected 'drburns-web'. Check .env.`,
  );
}

export const app: FirebaseApp = initializeApp(firebaseConfig);

// App Check (reCAPTCHA Enterprise), provisioned 2026-09-22. The site key is PUBLIC: it is bound
// to this web app's registered domains and carries no secret. Services stay UNENFORCED until the
// App Check metrics show the live traffic as verified; enforcing earlier would lock out real users.
// Skipped on localhost (the key's allowed domains exclude it); local runs that need a token set
// self.FIREBASE_APPCHECK_DEBUG_TOKEN before this module loads. Initialised synchronously, before
// any Firestore/Functions/Storage call, so no early request goes out without a token.
const APP_CHECK_SITE_KEY = "6Lfdy8ktAAAAABgNC9KMKGK5PRWiojUI0A70ksst";
if (
  typeof window !== "undefined" &&
  !/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname)
) {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(APP_CHECK_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch {
    // Hardening layer, never a boot dependency: a failure here must not blank the app.
  }
}
export const auth: Auth = getAuth(app);
export const db: Firestore = initializeFirestore(app, {
  ignoreUndefinedProperties: true,
});
export const storage: FirebaseStorage = getStorage(app);

export let analytics: Analytics | null = null;
isSupported().then((supported) => {
  if (!supported) return;
  // DRB-17: getAnalytics() does NOT throw on a missing measurementId — it proceeds and
  // injects <script src=".../gtag/js?id=undefined">, which every browser then blocks under
  // our CSP and reports as a console error on every page load. The previous try/catch here
  // was written believing GA4 would go "silently absent"; the catch never fired. Gate on
  // the value instead, so no analytics tag is injected until a real GA4 property exists.
  if (!firebaseConfig.measurementId) return;
  try {
    analytics = getAnalytics(app);
  } catch {
    // Genuinely unsupported environment — leave analytics null.
  }
});
